/* eslint-disable max-lines */
import {DialogueHistoryEntry, PlayerReduxState, PlayerStage} from "./types";
import {initialReduxState} from "./store";
import {PlayerReduxAction} from "./rootActions";
import {ChallengeMode, ChoiceUserInputBranchState} from "../../schema";
import {getCurrentScenario, getCurrentMaxIndex, isExamStage} from "./selectors";
import {analyticsSendEvent} from "../../libs/analytics";
import {_next, getBranch, getBranchActions} from "./utils";
import finishScenario from "./reducers/finishScenario";
import {isMultipleChoiceUserInputAction} from "../../types";

export default function rootReducer(state: PlayerReduxState | undefined, action: PlayerReduxAction): PlayerReduxState {
  if (state === undefined) {
    throw new Error("Cannot initialize without predefined state");
  }


  switch (action.type) {
    case "start": {
      const cleanedScenarios = state.scenarios.map((scenario) => ({
        ...scenario,
        actions: scenario.actions.filter((act) => act.id !== "feedback-intro" && act.id !== "feedback-content"),
      }));

      return {
        ...initialReduxState,

        client: state.client,
        audioManager: state.audioManager,
        toggleRecorder: state.toggleRecorder,
        raiseProblem: state.raiseProblem,

        challengeId: state.challengeId,
        challenge: state.challenge,
        scenarios: cleanedScenarios,
        isShared: state.isShared,
        isPreview: state.isPreview,
        isExternal: state.isExternal,
        externalUserId: state.externalUserId,
        utm: state.utm,
        recognitionPipeline: "new",

        ...action.payload,
      };
    }

    case "next": {
      const actions = getCurrentScenario(state).actions;
      const totalActions = actions.length;
      if (totalActions === 0) {
        return state;
      }
      const feedbackTextForHistory = state.adaptiveInput?.feedbackText;
      state = {...state, isHintConfirmation: false, isAutoUseHintOffered: false};

      if (state.branchId) {
        const rootAction = getCurrentScenario(state).actions[state.index];
        const isMultipleChoice = isMultipleChoiceUserInputAction(rootAction);
        if (isMultipleChoice) {
          let chosenBranches = new Set(state.chosenBranches);
          chosenBranches[state.chosenBranches.has(state.branchId) ? "delete" : "add"](state.branchId);
          return {
            ...state,
            branchId: undefined,
            branchIndex: undefined,
            chosenBranches,
          };
        }

        const branch = rootAction ? getBranch(rootAction, state.branchId) : undefined;
        const branchActions = branch ? getBranchActions(branch) : [];

        const nextBranchIndex = branchActions ? _next(branchActions, state.level, state.branchIndex!) : undefined;

        if (nextBranchIndex === undefined) {
          state = {
            ...state,
            branchId: undefined,
            branchIndex: undefined,
            chosenBranches: new Set([...Array.from(state.chosenBranches), state.branchId]),
          };
          if (branch && branch.__typename === "ChoiceUserInputBranch") {
            switch (branch?.state) {
              case ChoiceUserInputBranchState.CORRECT:
                // go to next action calc
                break;
              case ChoiceUserInputBranchState.INCORRECT:
                // stay on current action
                if (!state.lastUserInputResult?.ok) {
                  return state;
                }
                break;
              case ChoiceUserInputBranchState.INTERRUPT:
                state["interrupted"] = true;
                return state;
              default:
                console.error("Invalid ChoiceUserInputBranchState");
                break;
            }
          }
        } else {
          return {
            ...state,
            branchIndex: nextBranchIndex,
          };
        }
      }

      const currentAction = actions[state.index];
      const displayableTypenames = ["AvatarAction", "SystemAction", "UserInputAction", "ChoiceUserInputAction"];
      const isDisplayable = currentAction?.__typename && displayableTypenames.includes(currentAction.__typename);
      const skipRpgUserActionHistory =
        state.challenge.mode === ChallengeMode.RPG_MODE &&
        currentAction &&
        (currentAction.__typename === "UserInputAction" || currentAction.__typename === "ChoiceUserInputAction");
      let dialogueHistory = state.dialogueHistory ?? [];
      if (isDisplayable && currentAction && !skipRpgUserActionHistory) {
        const recognizedText = state.lastUserInputResult?.recognizedText;
        const entry: DialogueHistoryEntry = {
          action: currentAction,
          ...(feedbackTextForHistory ? {feedbackText: feedbackTextForHistory} : {}),
          ...(currentAction.__typename === "UserInputAction" && recognizedText != null
            ? {recognizedText}
            : {}),
          ...(currentAction.__typename === "ChoiceUserInputAction"
            ? {
                ...(state.chosenBranches.size > 0 ? {chosenBranchIds: Array.from(state.chosenBranches)} : {}),
                ...(recognizedText != null ? {recognizedText} : {}),
              }
            : {}),
        };
        dialogueHistory = [...dialogueHistory, entry];
      }

      state = {
        ...state,
        dialogueHistory,
        currentActionAttempts: 0,
        chosenBranches: new Set(),
        chosenBranchesData: new Map(),
        adaptiveInput: null,
      };

      const nextIndex = _next(getCurrentScenario(state).actions, state.level, state.index);
      if (nextIndex === undefined) {
        let newState = finishScenario(state);
        return newState;
      }

      const maxIndexes = [...state.maxIndexes];

      const prevMaxIndex = getCurrentMaxIndex(state);
      maxIndexes[state.scenarioIndex] = prevMaxIndex ? Math.max(nextIndex, prevMaxIndex) : nextIndex;

      const updatedScenarioStack = state.scenarioStack.map((entry) => {
        if (entry.scenarioIndex === state?.scenarioIndex) {
          return {...entry, index: nextIndex};
        }
        return entry;
      });

      return {
        ...state,
        index: nextIndex,
        mainScenarioLastIndex: getCurrentScenario(state).isMain ? nextIndex : state.mainScenarioLastIndex,
        scenarioStack: updatedScenarioStack,
        maxIndexes,
      };
    }

    case "back":
      for (let i = state.index - 1; i > 0; i--) {
        return {
          ...state,
          isHintConfirmation: false,
          index: i,
        };
      }

      return {
        ...state,
        isHintConfirmation: false,
        index: 0,
      };

    case "navigate": {
      return {
        ...state,
        isHintConfirmation: false,
        index: action.payload?.index ?? state.index,
      };
    }

    case "hint":
      if (state.isHintConfirmation || !isExamStage(state)) {
        return {
          ...state,
          isHintConfirmation: false,
          isAutoUseHintOffered: true,
          exposedActions: [...state.exposedActions, action.payload.actionId],
        };
      }
      return {
        ...state,
        isHintConfirmation: true,
        isAutoUseHintOffered: action.payload.isAutoUseHintOffered,
      };

    case "cancelConfirmHint":
      return {
        ...state,
        isHintConfirmation: false,
      };

    case "skipLevel":
      return {
        ...state,
        ...action.payload,
      };

    case "update":
      return {
        ...state,
        ...action.payload,
      };

    case "interrupt":
      return {
        ...state,

        interrupted: true,
        interruptReason: action.payload?.interruptReason ?? null,
      };

    case "startScenario": {
      state = {...state, isHintConfirmation: false};

      analyticsSendEvent("startScenario", {
        sessionId: state.sessionId!,
        scenarioId: action.payload.scenarioId,
      });

      const scenarioIndex = state.scenarios.findIndex(({id}) => id === action.payload.scenarioId);
      if (scenarioIndex === -1) {
        console.error(
          `Trying to start scenario with id "${action.payload.scenarioId}", which is not present in challenge`,
        );
      }
      if (state.scenarios[scenarioIndex].actions.length === 0) {
        return state;
      }
      let chosenBranches;
      if (action.payload.branchId) {
        chosenBranches = state.chosenBranches;
        chosenBranches.add(action.payload.branchId);
      }

      return {
        ...state,
        scenarioStack: [
          ...state.scenarioStack,
          {scenarioIndex: state.scenarioIndex, index: state.index, chosenBranches},
        ],
        scenarioIndex,
        index: 0,
        branchIndex: -1,
      };
    }

    case "notPassed": {
      return {
        ...state,
        currentActionAttempts: state.currentActionAttempts + 1,
      };
    }

    case "pause": {
      return {
        ...state,
        onPause: true,
      };
    }

    case "unpause": {
      return {
        ...state,
        onPause: false,
      };
    }

    case "triggerVideoPlayback": {
      return {
        ...state,
        onVideoPlay: true,
      };
    }

    case "resetVideoPlayback": {
      return {
        ...state,
        onVideoPlay: false,
      };
    }

    case "resetFinishScenarios": {
      return {
        ...state,
        finishedScenarios: [],
      };
    }

    case "playAdaptiveResponse": {
      if (!state.adaptiveInput || !state.adaptiveInput.responses) {
        return state;
      }

      return {
        ...state,
        adaptiveInput: {
          ...state.adaptiveInput,
          startedPlaying: true,
        },
      };
    }

    case "playSessionResultFeedback":
      return {
        ...state,
        stage: PlayerStage.Feedback,
        isFeedbackPlayed: false,
      };

    case "skipSessionResultFeedback":
      return {
        ...state,
        stage: PlayerStage.Result,
        isFeedbackPlayed: true,
        isFeedbackSkipped: true,
      };

    case "finishChallenge":
      return {
        ...state,
        stage: PlayerStage.Result,
        isFeedbackPlayed: true,
      };

    case "appendDialogueHistory": {
      return {
        ...state,
        dialogueHistory: [...(state.dialogueHistory ?? []), action.payload],
      };
    }

    case "appendAction": {
      const scenarioIndex = state.scenarioIndex;
      const scenario = state.scenarios[scenarioIndex];
      const updatedScenario = {
        ...scenario,
        actions: [...scenario.actions, ...((action as any).payload || [])],
      };

      const updatedScenarios = [...state.scenarios];
      updatedScenarios[scenarioIndex] = updatedScenario;

      return {
        ...state,
        scenarios: updatedScenarios,
        index: state.index >= scenario.actions.length ? scenario.actions.length : state.index,
      };
    }

    default:
      return state;
  }
}
